import React, { useState, useEffect, useCallback } from 'react';
import { CKEditor } from '@ckeditor/ckeditor5-react';
import ClassicEditor from '@ckeditor/ckeditor5-build-classic';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Switch,
  FormControlLabel,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Pagination,
  InputAdornment,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Alert,
  Snackbar,
  Grid,
  IconButton,
  Chip,
  Tooltip,
  CircularProgress,
} from '@mui/material';
import {
  Search as SearchIcon,
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Star as StarIcon,
  StarBorder as StarBorderIcon,
  ToggleOn as ToggleOnIcon,
  ToggleOff as ToggleOffIcon,
  Image as ImageIcon,
} from '@mui/icons-material';
import { blogService } from '../services/blogService';
import { Blog, CreateBlogData } from '../services/blogService';

const Blogs: React.FC = () => {
  const [blogs, setBlogs] = useState<Blog[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [open, setOpen] = useState(false);
  const [editingBlog, setEditingBlog] = useState<Blog | null>(null);
  const [formData, setFormData] = useState<CreateBlogData>({
    title: '',
    slug: '',
    content: '',
    description: '',
    publisherName: 'Panchkaram',
    category: '',
    sortOrder: 0,
    isActive: true,
    isFeatured: false,
  });
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');

  // Load blogs
  const loadBlogs = useCallback(async () => {
    setLoading(true);
    try {
      const response = await blogService.getBlogs({
        page,
        limit: 10,
        search: searchTerm || undefined,
        category: categoryFilter !== 'all' ? categoryFilter : undefined,
        isActive: statusFilter === 'active' ? true : statusFilter === 'inactive' ? false : undefined,
      });
      
      if (response.success) {
        setBlogs(response.data.blogs);
        setTotalPages(response.data.pagination.totalPages);
      } else {
        setError(response.message || 'Failed to load blogs');
      }
    } catch (err) {
      setError('Failed to load blogs');
    } finally {
      setLoading(false);
    }
  }, [page, searchTerm, categoryFilter, statusFilter]);

  useEffect(() => {
    loadBlogs();
  }, [loadBlogs]);

  // Handle dialog open
  const handleOpen = (blog?: Blog) => {
    if (blog) {
      setEditingBlog(blog);
      setFormData({
        title: blog.title,
        slug: blog.slug,
        content: blog.content,
        description: blog.description || '',
        publisherName: blog.publisherName,
        category: blog.category || '',
        sortOrder: blog.sortOrder,
        isActive: blog.isActive,
        isFeatured: blog.isFeatured,
      });
      setImagePreview(blog.bannerImage || null);
      setImageFile(null);
    } else {
      setEditingBlog(null);
      setFormData({
        title: '',
        slug: '',
        content: '',
        description: '',
        publisherName: 'Panchkaram',
        category: '',
        sortOrder: 0,
        isActive: true,
        isFeatured: false,
      });
      setImagePreview(null);
      setImageFile(null);
    }
    setOpen(true);
  };

  // Handle dialog close
  const handleClose = () => {
    setOpen(false);
    setEditingBlog(null);
    setFormData({
      title: '',
      slug: '',
      content: '',
      description: '',
      publisherName: 'Panchkaram',
      category: '',
      sortOrder: 0,
      isActive: true,
      isFeatured: false,
    });
    setImagePreview(null);
    setImageFile(null);
  };

  // Handle form input changes
  const handleInputChange = (field: keyof CreateBlogData, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    
    // Auto-generate slug from title
    if (field === 'title' && !editingBlog) {
      const slug = value.toLowerCase()
        .replace(/[^a-z0-9\s-]/g, '')
        .replace(/\s+/g, '-')
        .replace(/-+/g, '-')
        .trim();
      setFormData(prev => ({ ...prev, slug }));
    }
  };

  // Handle image upload
  const handleImageUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      if (!file.type.startsWith('image/')) {
        setError('Please select a valid image file');
        return;
      }
      
      if (file.size > 5 * 1024 * 1024) {
        setError('Image size must be less than 5MB');
        return;
      }
      
      setImageFile(file);
      
      const reader = new FileReader();
      reader.onload = (e) => {
        setImagePreview(e.target?.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  // Handle image removal
  const handleRemoveImage = () => {
    setImageFile(null);
    if (!editingBlog) {
      setImagePreview(null);
    }
  };

  // Handle save
  const handleSave = async () => {
    if (!formData.title || !formData.content) {
      setError('Title and content are required');
      return;
    }

    setLoading(true);
    try {
      let response;
      if (editingBlog) {
        response = await blogService.updateBlog(editingBlog.id, formData, imageFile || undefined);
      } else {
        response = await blogService.createBlog(formData, imageFile || undefined);
      }

      if (response.success) {
        setSuccess(editingBlog ? 'Blog updated successfully' : 'Blog created successfully');
        handleClose();
        loadBlogs();
      } else {
        setError(response.message || 'Failed to save blog');
      }
    } catch (err) {
      setError('Failed to save blog');
    } finally {
      setLoading(false);
    }
  };

  // Handle delete
  const handleDelete = async (id: number) => {
    if (!window.confirm('Are you sure you want to delete this blog?')) return;

    setLoading(true);
    try {
      const response = await blogService.deleteBlog(id);
      if (response.success) {
        setSuccess('Blog deleted successfully');
        loadBlogs();
      } else {
        setError(response.message || 'Failed to delete blog');
      }
    } catch (err) {
      setError('Failed to delete blog');
    } finally {
      setLoading(false);
    }
  };

  // Handle status toggle
  const handleToggleStatus = async (id: number, isActive: boolean) => {
    setLoading(true);
    try {
      const response = await blogService.toggleBlogStatus(id, isActive);
      if (response.success) {
        setSuccess(`Blog ${isActive ? 'activated' : 'deactivated'} successfully`);
        loadBlogs();
      } else {
        setError(response.message || 'Failed to update blog status');
      }
    } catch (err) {
      setError('Failed to update blog status');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4">Blog Management</Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => handleOpen()}
        >
          Add Blog
        </Button>
      </Box>

      {/* Filters */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                placeholder="Search blogs..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            {/* <Grid item xs={12} md={3}>
              <FormControl fullWidth>
                <InputLabel>Category</InputLabel>
                <Select
                  value={categoryFilter}
                  onChange={(e) => setCategoryFilter(e.target.value)}
                  label="Category"
                >
                  <MenuItem value="all">All Categories</MenuItem>
                  <MenuItem value="Wellness">Wellness</MenuItem>
                  <MenuItem value="Ayurveda">Ayurveda</MenuItem>
                  <MenuItem value="Yoga">Yoga</MenuItem>
                </Select>
              </FormControl>
            </Grid> */}
            <Grid item xs={12} md={3}>
              <FormControl fullWidth>
                <InputLabel>Status</InputLabel>
                <Select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  label="Status"
                >
                  <MenuItem value="all">All Status</MenuItem>
                  <MenuItem value="active">Active</MenuItem>
                  <MenuItem value="inactive">Inactive</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={2}>
              <Button
                variant="outlined"
                onClick={loadBlogs}
                fullWidth
              >
                Search
              </Button>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Blogs Table */}
      <Card>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Title</TableCell>
                <TableCell>Publisher</TableCell>
                {/* <TableCell>Category</TableCell> */}
                <TableCell>Views</TableCell>
                <TableCell>Comments</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Featured</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={8} align="center">
                    <CircularProgress />
                  </TableCell>
                </TableRow>
              ) : blogs.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} align="center">
                    No blogs found
                  </TableCell>
                </TableRow>
              ) : (
                blogs.map((blog) => (
                  <TableRow key={blog.id}>
                    <TableCell>
                      <Box>
                        <Typography variant="subtitle2" noWrap>
                          {blog.title}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {blog.slug}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>{blog.publisherName}</TableCell>
                    {/* <TableCell>
                      {blog.category && (
                        <Chip label={blog.category} size="small" />
                      )}
                    </TableCell> */}
                    <TableCell>{blog.viewCount}</TableCell>
                    <TableCell>{blog.commentCount}</TableCell>
                    <TableCell>
                      <Chip
                        label={blog.isActive ? 'Active' : 'Inactive'}
                        color={blog.isActive ? 'success' : 'default'}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      {blog.isFeatured ? (
                        <StarIcon color="primary" />
                      ) : (
                        <StarBorderIcon />
                      )}
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', gap: 1 }}>
                        <Tooltip title="Edit">
                          <IconButton
                            size="small"
                            onClick={() => handleOpen(blog)}
                          >
                            <EditIcon />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title={blog.isActive ? 'Deactivate' : 'Activate'}>
                          <IconButton
                            size="small"
                            onClick={() => handleToggleStatus(blog.id, !blog.isActive)}
                          >
                            {blog.isActive ? <ToggleOnIcon /> : <ToggleOffIcon />}
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Delete">
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => handleDelete(blog.id)}
                          >
                            <DeleteIcon />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
        
        {/* Pagination */}
        {totalPages > 1 && (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
            <Pagination
              count={totalPages}
              page={page}
              onChange={(_, newPage) => setPage(newPage)}
              color="primary"
            />
          </Box>
        )}
      </Card>

      {/* Add/Edit Dialog */}
      <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
        <DialogTitle>{editingBlog ? 'Edit Blog' : 'Add New Blog'}</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Title"
                  value={formData.title}
                  onChange={(e) => handleInputChange('title', e.target.value)}
                  required
                />
              </Grid>
              
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Slug"
                  value={formData.slug}
                  onChange={(e) => handleInputChange('slug', e.target.value)}
                  required
                />
              </Grid>
              
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Publisher Name"
                  value={formData.publisherName}
                  onChange={(e) => handleInputChange('publisherName', e.target.value)}
                  required
                />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  fullWidth
                  multiline
                  rows={2}
                  label="Description"
                  value={formData.description}
                  onChange={(e) => handleInputChange('description', e.target.value)}
                />
              </Grid>

              {/* Content with CKEditor */}
              <Grid item xs={12}>
                <Typography variant="h6" sx={{ mb: 1 }}>Content</Typography>
                <Box sx={{ border: '1px solid #ccc', borderRadius: 1 }}>
                  <CKEditor
                    editor={ClassicEditor}
                    data={formData.content}
                    onChange={(event, editor) => {
                      const data = editor.getData();
                      handleInputChange('content', data);
                    }}
                    config={{
                      toolbar: [
                        'heading', '|',
                        'bold', 'italic', 'underline', '|',
                        'bulletedList', 'numberedList', '|',
                        'link', 'blockQuote', '|',
                        'undo', 'redo'
                      ],
                      placeholder: 'Write your blog content here...'
                    }}
                  />
                </Box>
              </Grid>

              {/* Banner Image Upload */}
              <Grid item xs={12}>
                <Typography variant="h6" sx={{ mb: 1 }}>Banner Image</Typography>
                {imagePreview && (
                  <Box sx={{ mb: 2, textAlign: 'center' }}>
                    <img src={imagePreview} alt="Preview" style={{ maxWidth: '100%', maxHeight: 200, borderRadius: 8, border: '1px solid #ddd' }} />
                    <Box sx={{ mt: 1 }}>
                      <Button size="small" color="error" onClick={handleRemoveImage}>Remove Image</Button>
                    </Box>
                  </Box>
                )}
                <input accept="image/*" style={{ display: 'none' }} id="banner-image-upload" type="file" onChange={handleImageUpload} />
                <label htmlFor="banner-image-upload">
                  <Button variant="outlined" component="span" startIcon={<ImageIcon />} fullWidth>
                    {imagePreview ? 'Change Image' : 'Upload Image'}
                  </Button>
                </label>
                <Typography variant="caption" display="block" sx={{ mt: 1 }}>
                  Supported formats: JPG, PNG, GIF. Max size: 5MB
                </Typography>
              </Grid>

              {/* Settings */}
              <Grid item xs={12}>
                <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={formData.isActive}
                        onChange={(e) => handleInputChange('isActive', e.target.checked)}
                      />
                    }
                    label="Active"
                  />
                  <FormControlLabel
                    control={
                      <Switch
                        checked={formData.isFeatured}
                        onChange={(e) => handleInputChange('isFeatured', e.target.checked)}
                      />
                    }
                    label="Featured"
                  />
                </Box>
              </Grid>
            </Grid>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose}>Cancel</Button>
          <Button onClick={handleSave} variant="contained" disabled={loading}>
            {loading ? 'Saving...' : editingBlog ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbars */}
      <Snackbar
        open={!!error}
        autoHideDuration={6000}
        onClose={() => setError(null)}
      >
        <Alert severity="error" onClose={() => setError(null)}>
          {error}
        </Alert>
      </Snackbar>
      
      <Snackbar
        open={!!success}
        autoHideDuration={6000}
        onClose={() => setSuccess(null)}
      >
        <Alert severity="success" onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Blogs;
